import { FormControlLabel, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormikProps } from 'formik';
import { useRecoilValue } from 'recoil';
import { themeModeAtom, themeModeEnum } from 'src/recoil/atoms/themeModeAtom';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import {
  FormikSwitch,
  FormikTextField,
} from '../../../common/formik-material-ui';
import { Business } from '../../../lib/kixie-entities/tables/Business';
import { BusinessHarassmentPolicy } from '../../../lib/kixie-entities/tables/BusinessHarassmentPolicy';
import { Cadence } from '../../../lib/kixie-entities/tables/Cadence';
import { CadenceHarassmentPolicy } from '../../../lib/kixie-entities/tables/CadenceHarassmentPolicy';
import InfoPanel from '../InfoPanel/InfoPanel';
import './App.css';

const useStyles = makeStyles((theme: Theme) => ({
  timeframes: {
    marginTop: theme.spacing(1), // space between fields
    marginLeft: theme.spacing(1), // space between fields
    width: '65px',
  },
  amount: {
    marginTop: '19.8px', // space between fields
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1), // space between fields
    width: '65px',
  },
  spacing: {
    marginTop: '13.5px',
  },
}));

export function HarassmentRules<T extends Cadence | Business>(props: {
  formik: FormikProps<T>;
  showBusinessHarasmentToggle?: boolean;
}) {
  const classes = useStyles();
  const themeMode = useRecoilValue(themeModeAtom);

  const useBusinessHarassmentPolicyEnabled =
    props.formik.values instanceof Cadence
      ? props.formik.values.useBusinessHarassmentPolicyEnabled // if cadence then see if we are using business harassment polcies
      : true; // always use for business

  const policyName = useBusinessHarassmentPolicyEnabled
    ? 'businessHarassmentPolicies'
    : 'cadenceHarassmentPolicies';

  return (
    <div className="container">
      {props.formik.values instanceof Cadence && (
        <InfoPanel>
          <div>
            Set your Cadence Harassment Rules on this tab. Toggle the Use
            Business Harassment Rules to ON to use the settings applied to your
            account (Manage {`>`} Account {`>`} Harassment Rules). Toggle to OFF
            to apply custom Call and SMS Harassment Rules for this Cadence. This
            Cadence will not exceed the number of attempts in the Amount field
            for the Days, Hours, and Minutes in the Per field.
          </div>
        </InfoPanel>
      )}
      {props.showBusinessHarasmentToggle && (
        <FormControlLabel
          control={
            <FormikSwitch
              name="useBusinessHarassmentPolicyEnabled"
              formik={props.formik}
              color="primary"
              type="checkbox"
              readOnly={
                props.showBusinessHarasmentToggle &&
                useBusinessHarassmentPolicyEnabled
              }
            />
          }
          label="Use Business Harassment Rules"
        />
      )}
      {(props.formik.values as any)[policyName]?.map(
        (
          value: CadenceHarassmentPolicy | BusinessHarassmentPolicy,
          index: number
        ) => (
          <div
            key={index}
            style={{ marginTop: value.channel === 'sms' ? 16 : 0 }}
          >
            <LabelledOutline
              key={index}
              label={value.channel === 'call' ? 'Call Rules' : 'SMS Rules'}
              backgroundColor={
                themeMode === themeModeEnum.DARK_MODE ? '#121c21' : '#fff'
              }
            >
              <div className="row">
                <FormikTextField
                  formik={props.formik}
                  className={classes.amount}
                  name={`${policyName}.${index}.frequency`}
                  label="Amount"
                  data-qa="harassment-call-amount"
                  margin="dense"
                  size="small"
                  InputProps={{
                    inputProps: {
                      min: 1,
                      max: 9999,
                    },
                    disabled:
                      props.showBusinessHarasmentToggle &&
                      useBusinessHarassmentPolicyEnabled,
                  }}
                  type="number"
                  variant="outlined"
                />
                <span style={{ marginTop: '13px' }} className="timeframe">
                  <LabelledOutline
                    label="Per"
                    customPadding="5px 8px"
                    backgroundColor={
                      themeMode === themeModeEnum.DARK_MODE ? '#121c21' : '#fff'
                    }
                  >
                    <FormikTextField
                      className={classes.timeframes}
                      formik={props.formik}
                      name={`${policyName}.${index}.days`}
                      label="Days"
                      data-qa="harassment-call-per-day"
                      margin="dense"
                      size="small"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 90,
                        },
                        disabled:
                          props.showBusinessHarasmentToggle &&
                          useBusinessHarassmentPolicyEnabled,
                      }}
                      type="number"
                      variant="outlined"
                    />
                    <FormikTextField
                      className={classes.timeframes}
                      formik={props.formik}
                      name={`${policyName}.${index}.hours`}
                      label="Hours"
                      data-qa="harassment-call-per-hour"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 23,
                        },
                        disabled:
                          props.showBusinessHarasmentToggle &&
                          useBusinessHarassmentPolicyEnabled,
                      }}
                      size="small"
                      type="number"
                      variant="outlined"
                    />
                    <FormikTextField
                      className={classes.timeframes}
                      formik={props.formik}
                      name={`${policyName}.${index}.minutes`}
                      label="Minutes"
                      data-qa="harassment-call-per-minute"
                      InputProps={{
                        inputProps: {
                          min: 0,
                          max: 59,
                        },
                        disabled:
                          props.showBusinessHarasmentToggle &&
                          useBusinessHarassmentPolicyEnabled,
                      }}
                      size="small"
                      type="number"
                      variant="outlined"
                    />
                  </LabelledOutline>
                </span>
              </div>
            </LabelledOutline>
          </div>
        )
      )}
    </div>
  );
}
